/**
 * Tooltip — rich, delayed, edge-aware tooltip component.
 *
 * Single source of truth for all editor tooltip styles and behavior.
 * Appears after `delay` ms (default 900), renders via React portal,
 * anchors ABOVE the trigger and flips BELOW if near the viewport top,
 * clamps horizontally so it never clips off screen, and draws a
 * directional arrow that tracks the trigger's center.
 *
 * Usage:
 *   import { Tooltip, tip } from '../../components/ui/Tooltip';
 *   import { IcSettings } from '../../components/ui/icons';
 *
 *   <Tooltip content={tip({ title: 'Settings', icon: <IcSettings size={13} />,
 *                           body: 'Open editor preferences.', shortcut: 'Ctrl+,' })}>
 *     <button ...><IcSettings size={17} /></button>
 *   </Tooltip>
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { color, font } from './theme';
import { useTheme } from './themeStore';

// ── Content definition ────────────────────────────────────────────────────────

export interface TipDef {
  /** Short label displayed as the header title (rendered uppercase). */
  title: string;
  /** Small icon left of the title — reuse the button's icon at size 13. */
  icon?: React.ReactNode;
  /** Plain-english explanation written for a first-time user. */
  body: string;
  /** Keyboard shortcut displayed as a styled <kbd>. */
  shortcut?: string;
  /** Optional word shown just before the shortcut <kbd> (e.g. "Shortcut").
   *  Off by default so existing tooltips keep showing only the bare key. */
  shortcutLabel?: string;
  /** Optional secondary callout — pro tip, mode description, or warning. */
  note?: string;
}

/** Build structured rich tooltip content from a definition object. */
export function tip(def: TipDef): React.ReactNode {
  return <TipContent {...def} />;
}

function TipContent({ title, icon, body, shortcut, shortcutLabel, note }: TipDef) {
  return (
    <>
      <div style={tipHeadStyle}>
        {icon && <span style={tipIconWrapStyle}>{icon}</span>}
        <span style={tipTitleStyle}>{title}</span>
        {shortcut && shortcutLabel && <span style={tipShortcutLabelStyle}>{shortcutLabel}</span>}
        {shortcut && <kbd style={tipKbdStyle}>{shortcut}</kbd>}
      </div>
      <p style={tipBodyStyle}>{body}</p>
      {note && <div style={tipNoteStyle}>💡 {note}</div>}
    </>
  );
}

// ── Tooltip component ─────────────────────────────────────────────────────────

const TIP_W = 256;
const GAP   = 8;   // gap between button edge and arrow tip
const ARR_H = 7;   // arrow triangle height

interface TPos { left: number; top: number; flip: boolean; arrowX: number; }

export interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactElement;
  /** Delay before the tooltip appears (ms). Default 900. */
  delay?: number;
  /** Extra styles merged into the wrapper element (e.g. flex `order`). The
   *  wrapper is the layout box around the trigger, so this is how callers
   *  position the trigger within a flex/grid parent. */
  style?: React.CSSProperties;
  /** Show even when the global "tooltips" preference is disabled. */
  force?: boolean;
  /** Keep the tooltip visible while the mouse is over the popup itself. */
  hoverable?: boolean;
}

export function Tooltip({ content, children, delay = 900, style, force, hoverable }: TooltipProps) {
  const [pos, setPos] = useState<TPos | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const wrapRef = useRef<HTMLDivElement>(null);
  // Track whether mouse is over the trigger OR the popup (for hoverable mode).
  const overTrigger = useRef(false);
  const overPopup   = useRef(false);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const show = useCallback(() => {
    // Respect the global "show tooltips" preference (Settings → UI).
    if (!force && !useTheme.getState().tooltipsEnabled) return;
    timer.current = window.setTimeout(() => {
      const el = wrapRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();

      const cx = r.left + r.width / 2;
      let left  = cx - TIP_W / 2;
      const flip = r.top < 160; // flip below when near top of screen

      left = Math.max(8, Math.min(left, window.innerWidth - TIP_W - 8));

      setPos({
        left,
        top: flip ? r.bottom + GAP : r.top - GAP,
        flip,
        arrowX: Math.max(14, Math.min(cx - left, TIP_W - 14)),
      });
    }, delay);
  }, [delay, force]);

  const hide = useCallback(() => {
    window.clearTimeout(timer.current);
    setPos(null);
  }, []);

  const onTriggerEnter = useCallback(() => {
    overTrigger.current = true;
    show();
  }, [show]);

  const onTriggerLeave = useCallback(() => {
    overTrigger.current = false;
    if (hoverable) {
      // Give the mouse a tick to enter the popup before hiding.
      timer.current = window.setTimeout(() => {
        if (!overPopup.current) hide();
      }, 80);
    } else {
      hide();
    }
  }, [hide, hoverable]);

  const onPopupEnter = useCallback(() => { overPopup.current = true; }, []);
  const onPopupLeave = useCallback(() => {
    overPopup.current = false;
    if (!overTrigger.current) hide();
  }, [hide]);

  return (
    <div ref={wrapRef} style={style} onMouseEnter={onTriggerEnter} onMouseLeave={onTriggerLeave}>
      {children}
      {pos && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed',
            zIndex: 99999,
            width: TIP_W,
            overflow: 'visible',
            pointerEvents: hoverable ? 'auto' : 'none',
            left: pos.left,
            top: pos.top,
            transform: pos.flip ? 'none' : 'translateY(-100%)',
          }}
          onMouseEnter={hoverable ? onPopupEnter : undefined}
          onMouseLeave={hoverable ? onPopupLeave : undefined}
        >
          {/* Inner wrapper carries the entrance animation (fade + slide-up,
              eased) so it composes cleanly with the outer positioning transform. */}
          <div style={tooltipAnimStyle}>
            {/* content box */}
            <div style={contentBoxStyle}>
              {content}
            </div>
            {/* directional arrow */}
            <div style={pos.flip ? arrowUp(pos.arrowX) : arrowDown(pos.arrowX)} />
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// ── Shared style objects ──────────────────────────────────────────────────────

const tooltipAnimStyle: React.CSSProperties = {
  position: 'relative',
  // Fade in + ease up from slightly below — elegant entrance.
  animation: 'tooltipIn 0.18s cubic-bezier(0.16, 1, 0.3, 1)',
};

const contentBoxStyle: React.CSSProperties = {
  background: 'oklch(0.09 calc(0.018 * var(--tint-c)) var(--tint-h) / 0.97)',
  border: '1px solid oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.32)',
  borderRadius: 10,
  boxShadow: '0 10px 36px rgba(0,0,0,0.72), 0 0 18px oklch(0.55 calc(0.16 * var(--accent-c)) var(--accent-h) / 0.10)',
  backdropFilter: 'blur(14px)',
  WebkitBackdropFilter: 'blur(14px)',
  overflow: 'hidden',
  fontFamily: font.sans,
};

const arrowBase: React.CSSProperties = {
  position: 'absolute',
  width: 0,
  height: 0,
  borderLeft: `${ARR_H}px solid transparent`,
  borderRight: `${ARR_H}px solid transparent`,
  pointerEvents: 'none',
};

// Points down (tooltip is above the button)
const arrowDown = (x: number): React.CSSProperties => ({
  ...arrowBase,
  bottom: -ARR_H,
  left: x - ARR_H,
  borderTop: `${ARR_H}px solid oklch(0.09 calc(0.018 * var(--tint-c)) var(--tint-h))`,
});

// Points up (tooltip is below the button)
const arrowUp = (x: number): React.CSSProperties => ({
  ...arrowBase,
  top: -ARR_H,
  left: x - ARR_H,
  borderBottom: `${ARR_H}px solid oklch(0.09 calc(0.018 * var(--tint-c)) var(--tint-h))`,
});

// ── Inner content styles ──────────────────────────────────────────────────────

const tipHeadStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '8px 10px 6px',
  borderBottom: '1px solid oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.14)',
};

const tipIconWrapStyle: React.CSSProperties = {
  color: 'var(--accent)',
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
};

const tipTitleStyle: React.CSSProperties = {
  flex: 1,
  fontSize: 11,
  fontWeight: 700,
  color: 'var(--accent)',
  fontFamily: font.sans,
  letterSpacing: 1.2,
  textTransform: 'uppercase',
};

const tipShortcutLabelStyle: React.CSSProperties = {
  fontSize: 8.5,
  fontWeight: 600,
  letterSpacing: 0.6,
  textTransform: 'uppercase',
  color: '#7A7099',
  fontFamily: font.sans,
  flexShrink: 0,
};

const tipKbdStyle: React.CSSProperties = {
  background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.8)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.40)',
  borderRadius: 4,
  padding: '1px 5px',
  fontSize: 9,
  fontFamily: 'monospace',
  color: color.inkDim,
  letterSpacing: 0.4,
  flexShrink: 0,
};

const tipBodyStyle: React.CSSProperties = {
  margin: 0,
  padding: '7px 10px 9px',
  fontSize: 11,
  color: color.inkDim,
  fontFamily: font.sans,
  lineHeight: 1.58,
};

const tipNoteStyle: React.CSSProperties = {
  margin: '0 10px 9px',
  padding: '4px 8px',
  background: 'oklch(0.55 calc(0.16 * var(--accent-c)) var(--accent-h) / 0.08)',
  borderLeft: '2px solid oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.4)',
  borderRadius: '0 4px 4px 0',
  fontSize: 10,
  color: color.accentSoft,
  fontFamily: font.sans,
  lineHeight: 1.5,
};
